import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { useMe, usePlaceOrder, useProduct, useQuote } from "../api/hooks";
import { NeedTelegram } from "../components/NeedTelegram";
import { ProductShot } from "../components/ProductShot";
import { QtyStepper } from "../components/QtyStepper";
import { EMPTY_VALUES, fieldFromApiPath, initialValues, toNewOrder, validate, type CheckoutValues } from "../content/checkout";
import { checkout as text, fallbackProduct } from "../content/site";
import { formatMoney } from "../lib/money";
import { hapticNotify, hasTelegramContext } from "../lib/telegram";

type TextKey = keyof CheckoutValues;

function Field({
  id,
  label,
  values,
  errors,
  set,
  type = "text",
  hint,
  autoComplete,
}: {
  id: TextKey;
  label: string;
  values: CheckoutValues;
  errors: Record<string, string>;
  set: (patch: Partial<CheckoutValues>) => void;
  type?: string;
  hint?: string;
  autoComplete?: string;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={values[id]}
        onChange={(event) => set({ [id]: event.target.value } as Partial<CheckoutValues>)}
        aria-invalid={errors[id] ? "true" : undefined}
      />
      {errors[id] ? <div className="field__error">{errors[id]}</div> : hint ? <div className="field__hint">{hint}</div> : null}
    </div>
  );
}

/**
 * Оформление заказа — экран 9 ТЗ: карточка товара, контакты, доставка,
 * количество, промокод, итого. Оплаты онлайн нет: после отправки заявка
 * уходит менеджеру, покупатель видит экран «Заказ получен».
 *
 * Количество пересчитывается сервером через /cart/quote: цена и остаток
 * могли измениться, и в заказ уходит строка из пересчёта.
 */
export function CheckoutScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const variantId = params.get("variant") ?? "";
  const [quantity, setQuantity] = useState(Math.max(1, Number(params.get("qty") ?? 1) || 1));

  const me = useMe();
  const productQuery = useProduct();
  const product = productQuery.data ?? fallbackProduct;
  const quote = useQuote(variantId, quantity);
  const placeOrder = usePlaceOrder();

  const [values, setValues] = useState<CheckoutValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const seeded = useRef(false);

  // Профиль приходит асинхронно — заполняем форму один раз.
  useEffect(() => {
    if (seeded.current || me.isPending) return;
    seeded.current = true;
    setValues(initialValues(me.data));
  }, [me.data, me.isPending]);

  /** Ключ идемпотентности живёт от первой попытки до успеха: повтор после сетевой ошибки не создаёт второй заказ. */
  const idempotencyKey = useRef("");

  const set = useCallback((patch: Partial<CheckoutValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch)) delete next[key];
      return next;
    });
  }, []);

  if (!hasTelegramContext()) return <NeedTelegram what="Заказ" />;
  if (!variantId) return <div className="state">Сначала выберите товар.</div>;
  if (quote.isPending) return <div className="state">Готовим заказ…</div>;
  if (!quote.data?.orderable) {
    return (
      <div className="state">
        {text.unavailable}
        <button type="button" className="button button--ghost button--inline" onClick={() => navigate("/")}>
          {text.backToProduct}
        </button>
      </div>
    );
  }

  const line = quote.data.lines.find((l) => l.quantity > 0)!;
  const fieldProps = { values, errors, set };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    const nextErrors = validate(values);
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      hapticNotify("error");
      document.getElementById(firstError)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setFormError("Проверьте выделенные поля.");
      return;
    }

    if (!idempotencyKey.current) idempotencyKey.current = crypto.randomUUID();

    try {
      const order = await placeOrder.mutateAsync({
        order: toNewOrder(values, [{ variantId: line.variantId, quantity: line.quantity }]),
        idempotencyKey: idempotencyKey.current,
      });
      idempotencyKey.current = "";
      hapticNotify("success");
      navigate(`/orders/${encodeURIComponent(order.orderNumber)}/placed`, { replace: true });
    } catch (error) {
      hapticNotify("error");
      if (error instanceof ApiError) {
        setErrors(Object.fromEntries(error.details.map((d) => [fieldFromApiPath(d.path), d.message])));
        setFormError(error.message);
      } else {
        setFormError("Не удалось оформить заказ. Попробуйте ещё раз.");
      }
    }
  };

  return (
    <form className="screen screen--plain" onSubmit={onSubmit} noValidate>
      <h1 className="screen__title">{text.title}</h1>

      <div className="row" style={{ alignItems: "center" }}>
        <ProductShot product={product} size="small" />
        <div className="row__body">
          <div className="row__title">{line.productTitle}</div>
          <div className="row__meta">{line.label}</div>
          <strong>{formatMoney(line.unitPrice)}</strong>
        </div>
      </div>

      <Field {...fieldProps} id="name" label={text.fields.name} autoComplete="name" />
      <Field {...fieldProps} id="phone" label={text.fields.phone} type="tel" autoComplete="tel" />
      <Field {...fieldProps} id="telegram" label={text.fields.telegram} hint={text.hints.telegram} />
      <Field {...fieldProps} id="city" label={text.fields.city} autoComplete="address-level2" />
      <Field {...fieldProps} id="address" label={text.fields.address} hint={text.hints.address} autoComplete="street-address" />

      <div className="field">
        <div className="row__between">
          <span className="field__label" style={{ marginBottom: 0 }}>{text.fields.quantity}</span>
          <QtyStepper value={quantity} max={product.maxQuantity} onChange={setQuantity} />
        </div>
        {line.status !== "ok" && (
          <div className="field__hint">Доступно {line.quantity} шт. — количество скорректировано.</div>
        )}
      </div>

      <Field {...fieldProps} id="promoCode" label={text.fields.promoCode} hint={text.hints.promoCode} />

      <div className="field">
        <label className="field__label" htmlFor="comment">{text.fields.comment}</label>
        <textarea id="comment" rows={2} value={values.comment} onChange={(e) => set({ comment: e.target.value })} />
      </div>

      <div className="totals">
        <div className="totals__line">
          <span>{line.productTitle} × {line.quantity}</span>
          <span>{formatMoney(quote.data.subtotal)}</span>
        </div>
        <div className="totals__line totals__line--final">
          <span>{text.total}</span>
          <span>{formatMoney(quote.data.total)}</span>
        </div>
      </div>

      {formError && <div className="alert" role="alert">{formError}</div>}

      <button type="submit" className="button" disabled={placeOrder.isPending}>
        {placeOrder.isPending ? text.submitting : text.submit}
      </button>
      <p className="legal-note">{text.legal}</p>
    </form>
  );
}
